import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { uploadChunks } from "../../components/kit/PdfImport";

const TYPES = [
  ["kit", "Travel kit / guide", "Layover kits, cruise port-day kits, city guides"],
  ["checklist", "Checklist / planner", "Tick-box lists and fill-in planners"],
  ["itinerary", "Itinerary", "Day-by-day plans with real timings"],
];

const TypePick = ({ type, setType }) => (
  <div className="grid md:grid-cols-3 gap-4 mb-10">
    {TYPES.map(([k, l, d]) => (
      <button key={k} data-testid={`ex-type-${k}`} onClick={() => setType(k)}
        className={`ex-card text-left p-5 transition-colors ${type === k ? "border-[#30271D] ring-1 ring-[#30271D]" : "hover:border-stone-400"}`}>
        <div className="ex-display text-xl">{l}</div>
        <div className="text-sm text-stone-500 mt-1">{d}</div>
      </button>
    ))}
  </div>
);

export default function ExtrasNew() {
  const nav = useNavigate();
  const [mode, setMode] = useState("ai");
  const [type, setType] = useState("kit");
  const [brief, setBrief] = useState("");
  const [files, setFiles] = useState([]);
  const [tpl, setTpl] = useState({});
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  useEffect(() => { http.get("/extras/templates").then(({ data }) => setTpl(data)).catch(() => {}); }, []);
  const create = async () => {
    setBusy(true);
    try {
      if (mode === "ai") {
        const { data } = await http.post("/extras/products", { product_type: type, brief });
        toast.success("Writing your product — this takes a few minutes");
        return nav(`/extras/products/${data.id}`);
      }
      const ids = [];
      for (let i = 0; i < files.length; i++) {
        ids.push(await uploadChunks(files[i], { bulk: true }, (p) => setPct(Math.round(((i + p / 100) / files.length) * 100))));
      }
      const { data } = await http.post("/extras/products/upload", { upload_ids: ids, product_type: type });
      toast.success(`${data.ids.length} product(s) being made from your PDFs`);
      nav(data.ids.length === 1 ? `/extras/products/${data.ids[0]}` : "/extras");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false); setPct(null);
  };
  const ready = mode === "ai" ? brief.trim().length >= 15 : files.length > 0;
  return (
    <div data-testid="ex-new" className="max-w-4xl">
      <div className="ex-eyebrow mb-3">New product</div>
      <h1 className="ex-display text-4xl sm:text-5xl lg:text-6xl font-semibold">What are we making today?</h1>
      <p className="text-stone-600 mt-4 mb-8 max-w-2xl">Write a new product with AI, or upload PDFs you've already made. Either way you get a store listing in AUD and an Instagram post with its own image.</p>
      <div className="inline-flex rounded-full bg-[#EFE9E0] p-1 mb-8">
        {[["ai", "Write with AI", Sparkles], ["upload", "Upload my PDFs", Upload]].map(([k, l, I]) => (
          <button key={k} data-testid={`ex-mode-${k}`} onClick={() => setMode(k)} className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-colors ${mode === k ? "bg-[#30271D] text-white" : "text-stone-600"}`}><I size={14} /> {l}</button>
        ))}
      </div>
      <TypePick type={type} setType={setType} />
      {mode === "ai" ? <>
        <div className="ex-eyebrow mb-2">Your brief</div>
        <textarea data-testid="ex-brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={6} placeholder="What should it cover, who is it for, anything to include or avoid…"
          className="w-full rounded-lg border border-stone-300 bg-white p-4 text-sm outline-none focus:border-[#8B6B4A]" />
        <div className="ex-eyebrow mt-6 mb-2">Start from a template</div>
        <div className="flex flex-wrap gap-2 mb-10">
          {Object.entries(tpl).map(([k, v]) => <button key={k} data-testid={`ex-template-${k.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setBrief(v)} className="ex-btn ex-btn-line">{k}</button>)}
        </div>
      </> : (
        <label data-testid="ex-upload-drop" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setFiles([...e.dataTransfer.files].filter((f) => /\.pdf$/i.test(f.name))); }}
          className="block cursor-pointer rounded-xl border-2 border-dashed border-stone-300 bg-white p-10 text-center mb-10 hover:border-[#8B6B4A] transition-colors">
          <input data-testid="ex-upload-input" type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFiles([...e.target.files])} />
          <FileText className="mx-auto text-[#8B6B4A]" />
          <div className="mt-2 font-medium">{files.length ? files.map((f) => f.name).join(", ") : "Drop your PDFs here or click to choose"}</div>
          <div className="text-sm text-stone-500 mt-1">Each PDF becomes its own product, restyled into the PB layout with a PB cover.</div>
          {pct !== null && <div className="text-sm text-[#8B6B4A] mt-2">Uploading {pct}%</div>}
        </label>
      )}
      <button data-testid="ex-create" disabled={!ready || busy} onClick={create} className="ex-btn ex-btn-dark px-7 py-3 disabled:opacity-40">
        {busy && <Loader2 size={15} className="animate-spin" />} {mode === "ai" ? "Create product" : "Upload & create"}
      </button>
    </div>
  );
}
